import { AccountInfo } from "@azure/msal-browser";

// Enhanced type definitions for authentication

export type MaybeAccount = AccountInfo | null;

export interface AuthConfig {
  clientId: string;
  authority: string;
  redirectUri: string;
  scopes: string[];
  knownAuthorities: string[];
}

export interface AuthResult {
  success: boolean;
  account?: AccountInfo;
  error?: AuthError;
}

export interface TokenResult {
  token: string | null;
  expiresOn?: Date;
  error?: AuthError;
}

export enum AuthErrorType {
  FRAME_BLOCKED = "FRAME_BLOCKED",
  REDIRECT_LOOP = "REDIRECT_LOOP",
  ACCESS_DENIED = "ACCESS_DENIED",
  PASSWORD_RESET_REQUIRED = "PASSWORD_RESET_REQUIRED",
  INTERACTION_IN_PROGRESS = "INTERACTION_IN_PROGRESS",
  MAX_RETRIES_EXCEEDED = "MAX_RETRIES_EXCEEDED",
  INITIALIZATION_FAILED = "INITIALIZATION_FAILED",
  TOKEN_ACQUISITION_FAILED = "TOKEN_ACQUISITION_FAILED",
  NETWORK_ERROR = "NETWORK_ERROR",
  CONFIGURATION_ERROR = "CONFIGURATION_ERROR",
}

export class AuthError extends Error {
  constructor(
    public type: AuthErrorType,
    message: string,
    public originalError?: Error,
    public retryable: boolean = false,
  ) {
    super(message);
    this.name = "AuthError";
  }

  static fromMsalError(error: any): AuthError {
    const message = error.message ?? error.errorMessage ?? "Unknown MSAL error";

    if (message.includes("X-Frame-Options")) {
      return new AuthError(
        AuthErrorType.FRAME_BLOCKED,
        "Frame-based authentication blocked",
        error,
      );
    }

    if (message.includes("reprocess") || message.includes("redirect loop")) {
      return new AuthError(
        AuthErrorType.REDIRECT_LOOP,
        "Redirect loop detected",
        error,
      );
    }

    if (error.errorCode === "access_denied") {
      if (message.includes("AADB2C90091")) {
        return new AuthError(
          AuthErrorType.ACCESS_DENIED,
          "Access denied - login required",
          error,
          true,
        );
      }
      if (message.includes("AADB2C90118")) {
        return new AuthError(
          AuthErrorType.PASSWORD_RESET_REQUIRED,
          "Password reset required",
          error,
        );
      }
    }

    if (error.errorCode === "interaction_in_progress") {
      return new AuthError(
        AuthErrorType.INTERACTION_IN_PROGRESS,
        "Authentication interaction in progress",
        error,
        true,
      );
    }

    if (error.name === "NetworkError" || message.includes("network")) {
      return new AuthError(
        AuthErrorType.NETWORK_ERROR,
        "Network error during authentication",
        error,
        true,
      );
    }

    return new AuthError(
      AuthErrorType.TOKEN_ACQUISITION_FAILED,
      message,
      error,
      true,
    );
  }
}

export enum AuthState {
  UNINITIALIZED = "UNINITIALIZED",
  INITIALIZING = "INITIALIZING",
  INITIALIZED = "INITIALIZED",
  LOGGING_IN = "LOGGING_IN",
  AUTHENTICATED = "AUTHENTICATED",
  ERROR = "ERROR",
  LOGGED_OUT = "LOGGED_OUT",
}

export enum AuthStoreType {
  User = "User",
  Hmb = "HMB",
  SignUp = "SignUp",
}

export interface AuthStoreState {
  state: AuthState;
  account: MaybeAccount;
  authStoreType?: AuthStoreType;
  error: AuthError | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

export interface RetryConfig {
  maxAttempts: number;
  baseDelay: number;
  maxDelay: number;
  backoffMultiplier: number;
}

export interface AuthServiceInterface {
  initialize(client: any, config: any, scopes: string[]): Promise<MaybeAccount>;
  login(): Promise<MaybeAccount>;
  silentLogin(): Promise<MaybeAccount>;
  logout(): Promise<void>;
  getAccessTokenAsync(): Promise<string | null>;
  getActiveAccount(): MaybeAccount;
  clearAuthenticationState(): void;
  handleRedirect(): Promise<any>;
}

export interface AuthStoreInterface {
  // State
  state: AuthState;
  account: MaybeAccount;
  error: AuthError | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  authStoreType?: AuthStoreType;

  // Methods
  setup(): Promise<AuthStoreType | undefined>;
  login(loginType: AuthStoreType): Promise<void>;
  silentLogin(loginType: AuthStoreType): Promise<void>;
  logout(): Promise<void>;
  getAccessTokenAsync(): Promise<string | null>;
  clearAuthenticationState(): Promise<void>;
  retryLastOperation?(): Promise<void>;
}
